import { FunctionalInterfaceInstanceData } from '@/backend/access/functionalInterfaceInstance/types';
import { InterfaceInstanceFunctionData } from '@/backend/access/interfaceInstanceFunction/types';
import { FunctionalInterfaceInstance } from '@/backend/models/functionalInterfaceInstance';
import { InterfaceInstanceFunction } from '@/backend/models/interfaceInstanceFunction';
import { FusaReply } from '@/types/replyTypes';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { FunctionalInterfaceInstanceController } from '../controllers/functionalInterfaceInstanceController';
import { InterfaceInstanceFunctionCreateRequest, InterfaceInstanceFunctionUpdateRequest } from '@/backend/express/controllers/interfaceInstanceFunctions/interfaceInstanceFunctionController';
import { InterfaceFunctionController } from '../controllers/interfaceInstanceFunctionController';
import { FunctionalInterface } from '@/backend/models/functionalInterface';
import { FunctionalInterfaceCreateRequest } from '@/backend/express/controllers/functionalInterfaceController';
import { FunctionalInterfaceController } from '../controllers/functionalInterfaceController';
import { FunctionalInterfaceVersion } from '@/backend/models/functionalInterfaceVersion';
import { FunctionalInterfaceVersionController } from '../controllers/functionalInterfaceVersionController';
import { FunctionalInterfaceParams } from '@/types/functionalInterfaceTypes';
import { VersionType } from '@/types/genericVersionTypes';

export interface FunctionalInterfacesStore {
  functionalInterfaces: FunctionalInterface[];
  functionalInterfaceVersions: FunctionalInterfaceVersion[];
  interfaceFunctions: InterfaceInstanceFunction[];
  functionalInterfaceInstances: FunctionalInterfaceInstance[];

  setFunctionalInterfaceInstances: (functionalInterfaces: FunctionalInterfaceInstanceData[]) => void;
  setInterfaceFunctions: (interfaceFunctions: InterfaceInstanceFunctionData[]) => void;
  createInterfaceFunction: (request: InterfaceInstanceFunctionCreateRequest) => Promise<FusaReply>;
  updateInterfaceFunction: (request: InterfaceInstanceFunctionUpdateRequest) => Promise<FusaReply | undefined>;
  deleteInterfaceFunction: (id: string) => Promise<FusaReply | undefined>;
  fetchInterfaceFunctionsByFunctionalInterfaceInstanceId: (functionalInterfaceId: string) => Promise<void>;
  readFunctionalInterfaceInstance: (id: string) => Promise<void>;
  fetchAllFunctionalInterfaces: () => Promise<void>;
  createFunctionalInterface: (request: FunctionalInterfaceCreateRequest) => Promise<void>;
  createNewVersion: (id: string) => Promise<FusaReply | undefined>;
  deleteFunctionalInterface: (id: string) => Promise<FusaReply | undefined>;
  readFunctionalInterface: (id: string) => Promise<void>;
  updateFunctionalInterface: (functionalInterface: FunctionalInterfaceParams) => Promise<void>;
  getInterfaceFunctionsByFunctionalInterfaceId: (functionalInterfaceId: string) => InterfaceInstanceFunction[];
  deleteFunctionalInterfaceVersion: (id: string) => Promise<FusaReply | undefined>;
  publishFunctionalInterfaceVersion: (versionId: string, functionalInterfaceId: string, versionType: VersionType) => Promise<void>;
  fetchAllLatestVersions: () => Promise<void>;
  addLatestVersionToProcedure: (functionalInterfaceId: string, procedureId: string) => Promise<void>;
  readVersionsForProcedure: (procedureId: string) => Promise<void>;
  reset: () => void;
}

const initialState = {
  functionalInterfaces: [] as FunctionalInterface[],
  functionalInterfaceVersions: [] as FunctionalInterfaceVersion[],
  interfaceFunctions: [] as InterfaceInstanceFunction[],
  functionalInterfaceInstances: [] as FunctionalInterfaceInstance[],
};

export const useFunctionalInterfacesStore = create<FunctionalInterfacesStore>()(immer((set, get) => ({
  ...initialState,
  setFunctionalInterfaceInstances: (functionalInterfacesData) => {
    const builtFunctionalInterfaces = functionalInterfacesData.map((data) => new FunctionalInterfaceInstance(data));
    set({ functionalInterfaceInstances: builtFunctionalInterfaces });
  },
  setInterfaceFunctions: (interfaceFunctionsData) => {
    const builtInterfaceFunctions = interfaceFunctionsData.map((data) => new InterfaceInstanceFunction(data));
    set({ interfaceFunctions: builtInterfaceFunctions });
  },
  createInterfaceFunction: async (request) => {
    const response = await new InterfaceFunctionController().createInterfaceFunction(request);

    if(response.errors) return response;

    const { interfaceFunctions } = get();
    const newInterfaceFunction = new InterfaceInstanceFunction(response.interfaceFunction);
    set({ interfaceFunctions: [...interfaceFunctions, newInterfaceFunction] });

    return response;
  },
  updateInterfaceFunction: async (request) => {
    const response = await new InterfaceFunctionController().updateInterfaceFunction(request);

    if (response.interfaceFunction) {
      const { interfaceFunctions } = get();
      const updatedInterfaceFunction = new InterfaceInstanceFunction(response.interfaceFunction);
      set({
        interfaceFunctions: interfaceFunctions.map(func =>
          func.id === response.interfaceFunction?.id ? updatedInterfaceFunction : func
        )
      });
    }

    return response;
  },

  deleteInterfaceFunction: async (id) => {
    const response = new InterfaceFunctionController().deleteInterfaceFunction(id);

    const { interfaceFunctions } = get();
    set({ interfaceFunctions: interfaceFunctions.filter(func => func.id !== id) });

    return response;
  },
  fetchInterfaceFunctionsByFunctionalInterfaceInstanceId: async (functionalInterfaceId) => {
    const response = await new InterfaceFunctionController().readInterfaceFunctionsByFunctionalInterfaceInstanceId(functionalInterfaceId);

    if (response.errors) {
      return;
    }

    const { interfaceFunctions } = get();
    const existingFunctions = interfaceFunctions.filter(func => func.functionalInterfaceId !== functionalInterfaceId);
    const newFunctions = (response.interfaceFunctions || []).map((data: InterfaceInstanceFunctionData) => new InterfaceInstanceFunction(data));
    set({ interfaceFunctions: [...existingFunctions, ...newFunctions] });
  },
  readFunctionalInterfaceInstance: async (id) => {
    const response = await new FunctionalInterfaceInstanceController().readFunctionalInterface(id);

    if (response.errors) return;

    if (response.functionalInterfaceInstance) {
      set({ functionalInterfaceInstances: [new FunctionalInterfaceInstance(response.functionalInterfaceInstance)] });
    }
    if (response.version) {
      set({ functionalInterfaceVersions: [new FunctionalInterfaceVersion(response.version)] });
    }
    if (response.functionalInterface) {
      set({ functionalInterfaces: [new FunctionalInterface(response.functionalInterface)] });
    }
  },
  getInterfaceFunctionsByFunctionalInterfaceId: (functionalInterfaceId) => {
    const { interfaceFunctions } = get();
    return interfaceFunctions.filter(func => func.functionalInterfaceId === functionalInterfaceId);
  },
  reset: () => {
    set(initialState);
  },
  fetchAllFunctionalInterfaces: async () => {
    const response = await new FunctionalInterfaceController().readAll();

    if (response.errors) return;

    set({ functionalInterfaces: response.functionalInterfaces.map((functionalInterface) => new FunctionalInterface(functionalInterface)) });
  },
  createFunctionalInterface: async (request) => {
    const response = await new FunctionalInterfaceController().create(request);

    if (response.errors) return;

    set({ functionalInterfaces: [...get().functionalInterfaces, new FunctionalInterface(response.functionalInterface)] });
    set({ functionalInterfaceInstances: [...get().functionalInterfaceInstances, new FunctionalInterfaceInstance(response.instance)] });
  },
  createNewVersion: async (id) => {
    const response = await new FunctionalInterfaceController().createNewVersion(id);

    if (response.errors) return response;

    set({ 
      functionalInterfaceVersions: [new FunctionalInterfaceVersion(response.version), ...get().functionalInterfaceVersions],
      functionalInterfaceInstances: [new FunctionalInterfaceInstance(response.instance), ...get().functionalInterfaceInstances]
    });
  },
  deleteFunctionalInterface: async (id) => {
    const response = await new FunctionalInterfaceController().delete(id);

    if (response.errors) return response;

    set({ functionalInterfaces: get().functionalInterfaces.filter((functionalInterface) => functionalInterface.id !== id) });
  },
  readFunctionalInterface: async (id) => {
    const response = await new FunctionalInterfaceController().read(id);

    if (response.errors) return;

    set({ 
      functionalInterfaces: [new FunctionalInterface(response.functionalInterface)],
      functionalInterfaceVersions: response.versions.map((version) => new FunctionalInterfaceVersion(version)),
      functionalInterfaceInstances: response.instances.map((instance) => new FunctionalInterfaceInstance(instance))
    });
  },
  updateFunctionalInterface: async (functionalInterface) => {
    const response = await new FunctionalInterfaceController().update({ functionalInterface });

    if (response.errors) return;

    const { functionalInterfaces } = get();
    set({ functionalInterfaces: functionalInterfaces.map((functionalInterface) => functionalInterface.id === response.functionalInterface?.id ? new FunctionalInterface(response.functionalInterface) : functionalInterface) });
  },
  deleteFunctionalInterfaceVersion: async (id) => {
    const response = await new FunctionalInterfaceVersionController().delete(id);

    if (response.errors) return response;

    set({ 
      functionalInterfaceVersions: get().functionalInterfaceVersions.filter((version) => version.id !== id),
      functionalInterfaceInstances: get().functionalInterfaceInstances.filter((instance) => instance.versionId !== id)
    });
  },
  publishFunctionalInterfaceVersion: async (versionId, functionalInterfaceId, versionType) => {
    const response = await new FunctionalInterfaceVersionController().publish(versionId, functionalInterfaceId, versionType);

    if (response.errors) return;

    await get().readFunctionalInterface(functionalInterfaceId);
  },
  fetchAllLatestVersions: async () => {
    const response = await new FunctionalInterfaceController().readAllLatestVersions();

    if (response.errors) return;

    set({ 
      functionalInterfaces: response.functionalInterfaces.map((functionalInterface) => new FunctionalInterface(functionalInterface)), 
    });
  },
  addLatestVersionToProcedure: async (functionalInterfaceId, procedureId) => {
    const response = await new FunctionalInterfaceController().addLatestVersionToProcedure(functionalInterfaceId, procedureId);

    if (response.errors) return;

    set({ functionalInterfaceVersions: [new FunctionalInterfaceVersion(response.version), ...get().functionalInterfaceVersions] });
  },
  readVersionsForProcedure: async (procedureId) => {
    const response = await new FunctionalInterfaceVersionController().readAllForProcedure(procedureId);

    if (response.errors) return;

    set({ functionalInterfaceVersions: response.versions.map((version) => new FunctionalInterfaceVersion(version)) });
  },
})));
